import { useEffect, useState } from "react";
import { FaDownload } from "react-icons/fa";
import { getAvatarUrl, getPOReceiptUrl, getRealFileName } from "src/Common/common";
import { paymentItems } from "src/Common/contants";
import { downloadImage, handleError, isAccountant, isClericalOver } from "src/Common/function";
import PencilButton from "src/Components/Common/pencilbutton";
import Pagination from "src/Components/Pagination";
import POStatusComponent from "src/Pages/PO/Components/Status/postatuscomponent";
import POService from "src/Services/API/po.service";

function Pos({
  woId,
  navigatePODetail,
  poEditHandle,
  setPreviewImages,
  setPreviewImageActiveIndex,
  setIsImageViewerShown,
}) {
  const [pos, setPos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    setShowAll(false);
    setPageNumber(0);
  }, [woId]);

  useEffect(() => {
    setIsLoading(true);
    const request = showAll
      ? POService.getPurchaseOrdersByWoId(woId)
      : POService.getPurchaseOrdersByWoIdPaginated(woId, pageNumber);

    request
      .then((response) => {
        if (response.status === 200) {
          const responseData = response?.data;
          const orders = Array.isArray(responseData)
            ? responseData
            : Array.isArray(responseData?.orders)
              ? responseData.orders
              : [];

          if (showAll) {
            const totalCount = orders.length;
            setPos(orders);
            setTotalItems(totalCount);
            setPageSize(totalCount > 0 ? totalCount : 10);
            setTotalPages(1);
            setPageNumber(0);
            return;
          }

          const apiTotalItems = Number(response?.data?.totalCount ?? response?.data?.totalItems ?? 0);
          const apiPageSizeRaw = Number(response?.data?.pageSize ?? response?.data?.limit);
          const apiTotalPagesRaw = Number(response?.data?.totalPages);
          const inferredFirstPageSize = pageNumber === 0 && orders.length > 0 ? orders.length : null;

          setPos(orders);
          setTotalItems(apiTotalItems);
          setPageSize((prevPageSize) => {
            if (Number.isFinite(apiPageSizeRaw) && apiPageSizeRaw > 0) {
              return apiPageSizeRaw;
            }
            if (inferredFirstPageSize) {
              return inferredFirstPageSize;
            }
            return prevPageSize > 0 ? prevPageSize : 10;
          });
          setTotalPages((prevTotalPages) => {
            if (Number.isFinite(apiTotalPagesRaw) && apiTotalPagesRaw > 0) {
              return apiTotalPagesRaw;
            }

            const resolvedPageSize =
              Number.isFinite(apiPageSizeRaw) && apiPageSizeRaw > 0
                ? apiPageSizeRaw
                : inferredFirstPageSize
                  ? inferredFirstPageSize
                  : pageSize > 0
                    ? pageSize
                    : 10;
            const computedPages = resolvedPageSize > 0 ? Math.ceil(apiTotalItems / resolvedPageSize) : 1;
            if (computedPages > 0) {
              return computedPages;
            }
            return prevTotalPages > 0 ? prevTotalPages : 1;
          });
        }
      })
      .catch((error) => handleError(error))
      .finally(() => setIsLoading(false));
  }, [woId, pageNumber, showAll]);

  const hasPurchaseOrders = totalItems > 0 || pos.length > 0;

  return (
    <>
      {!isLoading && !hasPurchaseOrders && (
        <div className="w-full py-2">
          <span className="font-OpenSansRegular font-[400] text-[12px] text-gray-500">No purchase orders</span>
        </div>
      )}

      {pos.map((po, index) => (
        <div key={index} className="w-full mb-5 border-solid border-b-[1px] border-borderColor">
          <div className="w-full h-[30px]  flex items-center justify-between px-3">
            <div className="flex items-center gap-2">
              <span className="font-OpenSansRegular font-[700] text-[16px] text-titleColor ">PO#:</span>
              <span
                onClick={() => navigatePODetail(po)}
                className="font-OpenSansRegular font-[500] text-[16px] text-titleColor underline cursor-pointer"
              >
                {po.number}
              </span>
            </div>
            <POStatusComponent value={po.status} />
          </div>
          <div className="w-full   flex flex-col items-center gap-3 px-3 py-3 relative">
            {(isClericalOver() || isAccountant()) && (
              <div className="absolute top-[5px] right-[5px]">
                <PencilButton clicked={() => poEditHandle(po)} />
              </div>
            )}

            <div className="w-[150px] absolute top-0 right-[40px] h-full py-3 flex flex-col">
              <p className="font-OpenSansRegular font-[700] text-[12px] text-titleColor">Attachments</p>
              <div className="w-full flex-auto overflow-auto ">
                {po.attachments &&
                  po.attachments.map((file, index) => {
                    return (
                      <div className="w-full h-[25px] flex items-center gap-3 " key={index}>
                        <span
                          onClick={() => {
                            let temp = [];
                            for (let attachment of po.attachments) {
                              temp.push({
                                src: getPOReceiptUrl(attachment),
                                alt: "",
                              });
                            }
                            setPreviewImages(temp);
                            setPreviewImageActiveIndex(index);
                            setIsImageViewerShown(true);
                          }}
                          className="font-OpenSansRegular font-[400] text-[11px] text-titleColor cursor-pointer"
                        >
                          {getRealFileName(file)}
                        </span>
                        <div onClick={() => downloadImage(getPOReceiptUrl(file), file)} className="cursor-pointer">
                          <FaDownload color="#010D3D" size={13} />
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            <div className="w-full flex items-center pr-[50px]">
              <div className="flex items-center gap-1">
                <span className="font-OpenSansRegular font-[700] text-[12px] text-titleColor">PO#: </span>
                <span className="font-OpenSansRegular text-[12px] text-titleColor">{po.number}</span>
              </div>
              <div className="flex items-center gap-1 ml-[30px]">
                <span className="font-OpenSansRegular font-[700] text-[12px] text-titleColor">Type: </span>
                <span className="font-OpenSansRegular text-[12px] text-titleColor">{paymentItems[po.paymentType]}</span>
              </div>
              {po.issuedUser && (
                <div className="flex items-center gap-2 ml-[30px]">
                  <span className="font-OpenSansRegular font-[700] text-[12px] text-titleColor w-[110px]">
                    Issued For:{" "}
                  </span>
                  <div className="w-full flex items-center gap-1">
                    <img src={getAvatarUrl(po.issuedUser.avatar)} className="w-[16px] h-[16px] rounded-[8px]" />
                    <span className="font-OpenSansRegular font-[400] text-[11px] text-titleColor">{po.issuedUser.name}</span>
                  </div>
                </div>
              )}
            </div>
            <div className="w-full flex items-center pr-[50px]">
              <div className="flex items-center gap-1">
                <span className="font-OpenSansRegular font-[700] text-[12px] text-titleColor">Vendor: </span>
                <span className="font-OpenSansRegular text-[12px] text-titleColor">{po.vendor}</span>
              </div>
            </div>
            <div className="w-full flex items-center justify-between">
              <div className="flex flex-col  gap-1">
                <span className="font-OpenSansRegular font-[700] text-[12px] text-titleColor text-left">Description</span>
                <span className="font-OpenSansRegular text-[12px] text-titleColor break-all ">{po.description}</span>
              </div>
            </div>

            {/* Do not delete the below code for the future */}

            {/* <div className="w-full flex flex-col justify-between">
              <div className="flex flex-col  gap-1">
                <span className="font-OpenSansRegular font-[700] text-[12px] text-titleColor text-left">Items</span>
                {po.poItems.map((item, index) => (
                  <div key={index} className="w-full flex items-center justify-between h-[30px]">
                    <span className="font-OpenSansRegular font-[400] text-[12px] text-titleColor">
                      {item.description}{" "}
                    </span>
                    {item.attachment && (
                      <a
                        href={getUploadUrl(item.attachment)}
                        target="_blank"
                        download={getRealFileName(item.attachment)}
                      >
                        <span className="font-OpenSansRegular font-[400] text-[11px] text-titleColor">
                          {getRealFileName(item.attachment)}
                        </span>
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>   */}
          </div>
        </div>
      ))}
      {hasPurchaseOrders && (
        <div className="w-full flex items-center gap-3">
          <button
            type="button"
            onClick={() => {
              setShowAll((prev) => !prev);
              setPageNumber(0);
            }}
            className="py-1 px-3 rounded-[12px] border-solid border-[1px] border-borderColor font-OpenSansRegular text-[11px] text-titleColor whitespace-nowrap"
          >
            {showAll ? "Show Pagination" : "Show All"}
          </button>
          <Pagination
            currentPage={pageNumber + 1}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={(page) => setPageNumber(page - 1)}
          />
        </div>
      )}
    </>
  );
}

export default Pos;
